import React, { useCallback, useState } from 'react';
import noop from 'lodash/noop';
import type { Attributes } from '@atlassian/jira-product-analytics-bridge';
import JiraFullscreenButton from './main.tsx';
// eslint-disable-next-line @atlassian/eng-health/no-barrel-files/disallow-reexports
export { useIsFullscreen } from './common/utils/hooks/use-is-fullscreen/index.tsx';

type Props = {
	/* testId: This is the data-testId for the button component. This can be used for testing purposes. */
	testId: string;
	/*  getCustomRefs: This is a function that gets an array of elements that neeeds to be styled differently when going fullscreen.
	 * The class "fullscreen" is added to the elements when the button is toggled on, and removed when toggled off.
	 * Consumers can apply their own styles with the "fullscreen" class in their own package.
	 * @deprecated See readme for details on how to customise components in fullscreen */
	getCustomRefs: () => (HTMLElement | null)[];
	/* analyticsAttributes: Additional analytics attributes */
	analyticsAttributes: Attributes;
	/* onToggle: Callback function whenever the button is toggled.
	 * The fullscreen value is passed.
	 * The global style sheet can be accessed to add extra styles. */
	onToggle: (isFullScreen: boolean, globalStyle?: HTMLElement) => void;
	/* withContainerStyles: Boolean value determining whether the default shadow container styling is applied. */
	withContainerStyles?: boolean;
	/* Sets whether the fullscreen button begins active or inactive. */
	defaultFullscreen?: boolean;
	/* Sets whether the fullscreen button is active or inactive. */
	isFullscreen?: boolean;
	/* Set whether to render the button
	 * Unfortunately due to side effects in the FullscreenButton code we can't just conditionally render the button,
	 * We have to conditionally render the contents within the button */
	shouldRenderContent?: boolean;
	// Indicates whether the IconButton should have a subtle appearance
	isSubtle?: boolean;
	// Determines whether the icon button should be hidden from assistive technology
	isHidden?: boolean;
	// Controls whether keyboard shortcuts are enabled for the fullscreen functionality
	hasShortcut?: boolean;
};

const FullscreenButton = ({
	testId,
	onToggle,
	getCustomRefs,
	analyticsAttributes,
	withContainerStyles = true,
	defaultFullscreen = false,
	isFullscreen: isFullscreenProp,
	shouldRenderContent = true,
	isSubtle,
	isHidden,
	hasShortcut = true,
}: Props) => {
	const [isFullscreenState, setIsFullscreenState] = useState(isFullscreenProp ?? defaultFullscreen);
	const isFullscreen = isFullscreenProp ?? isFullscreenState;

	const handleToggle = useCallback(
		(wasFullscreen: boolean, globalStyle?: HTMLElement) => {
			onToggle(wasFullscreen, globalStyle);
			setIsFullscreenState(!wasFullscreen);
		},
		[onToggle, setIsFullscreenState],
	);

	return (
		<JiraFullscreenButton
			testId={testId}
			onToggle={handleToggle}
			getCustomRefs={getCustomRefs}
			analyticsAttributes={analyticsAttributes}
			withContainerStyles={withContainerStyles}
			defaultFullscreen={defaultFullscreen}
			isFullscreen={isFullscreen}
			shouldRenderContent={shouldRenderContent}
			isSubtle={isSubtle}
			isHidden={isHidden}
			hasShortcut={hasShortcut}
		/>
	);
};

FullscreenButton.defaultProps = {
	onToggle: noop,
	getCustomRefs: () => [],
	analyticsAttributes: {},
};

export default FullscreenButton;
